/**
 * Compliance → Rectification (GDPR Art 16 / DPDPA §13).
 *
 * Operator submits a request (principal identifier + kind + field
 * corrections) → controller hashes + snapshots in-scope assets → DPO
 * approves → controller records per-asset update-intent with the field
 * list. Live DB-level UPDATE statements run when the customer-VPC
 * scanner pod consumes the connector-side rectification command on its
 * next reconcile (paired with the Erasure tombstoning path).
 *
 * Mirrors Erasure.tsx - same KPI bento + submit form + queue + detail
 * modal - with one additional input: a dynamic field/value pair list
 * for the corrections map. By design these two surfaces look the same
 * so a DPO can flip between them in muscle memory.
 */
import { useCallback, useEffect, useState } from 'react';
import { motion } from 'motion/react';
import {
  RefreshCw,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock,
  XCircle,
  Plus,
  ShieldCheck,
  Play,
  Eye,
  Ban,
  Edit3,
  Trash2,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/primitives';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';
import { ErrorState, LoadingState } from '@foctta/ui-shell';
import { KpiCard } from '@/components/viz/KpiCard';
import {
  listRectificationJobs,
  createRectificationRequest,
  approveRectificationJob,
  executeRectificationJob,
  type RectificationJob,
  type RectificationJobStatus,
  type RectificationIdentifierKind,
} from '@/lib/api';

const STATUS_VARIANT: Record<RectificationJobStatus, 'success' | 'warning' | 'critical' | 'glass'> = {
  pending: 'glass',
  approved: 'warning',
  running: 'warning',
  completed: 'success',
  failed: 'critical',
  cancelled: 'critical',
};

const STATUS_ICON: Record<RectificationJobStatus, typeof CheckCircle2> = {
  pending: Clock,
  approved: ShieldCheck,
  running: Loader2,
  completed: CheckCircle2,
  failed: XCircle,
  cancelled: Ban,
};

const IDENTIFIER_OPTIONS: ReadonlyArray<{ value: RectificationIdentifierKind; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'aadhaar', label: 'Aadhaar' },
  { value: 'pan', label: 'PAN' },
  { value: 'customer_number', label: 'Customer number' },
  { value: 'custom', label: 'Custom identifier' },
];

interface CorrectionRow {
  /** Stable local id for React key. */
  key: string;
  field: string;
  value: string;
}

function newCorrectionRow(): CorrectionRow {
  return {
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    field: '',
    value: '',
  };
}

export function ComplianceRectification(): JSX.Element {
  const [jobs, setJobs] = useState<RectificationJob[]>([]);
  const [counts, setCounts] = useState({
    pending: 0, approved: 0, running: 0, completed: 0, failed: 0, cancelled: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Request form
  const [identifierKind, setIdentifierKind] = useState<RectificationIdentifierKind>('email');
  const [principalIdentifier, setPrincipalIdentifier] = useState('');
  const [requestReason, setRequestReason] = useState('');
  const [correctionRows, setCorrectionRows] = useState<CorrectionRow[]>([newCorrectionRow()]);
  const [submitting, setSubmitting] = useState(false);

  // Detail modal
  const [detailJob, setDetailJob] = useState<RectificationJob | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const r = await listRectificationJobs({});
      setJobs(r.jobs);
      setCounts(r.byStatus);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to load rectification jobs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  function updateCorrectionField(key: string, field: 'field' | 'value', next: string): void {
    setCorrectionRows((rows) =>
      rows.map((r) => (r.key === key ? { ...r, [field]: next } : r)),
    );
  }

  function addCorrectionRow(): void {
    setCorrectionRows((rows) => [...rows, newCorrectionRow()]);
  }

  function removeCorrectionRow(key: string): void {
    setCorrectionRows((rows) => (rows.length === 1 ? rows : rows.filter((r) => r.key !== key)));
  }

  function collectCorrections(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const r of correctionRows) {
      const field = r.field.trim();
      const value = r.value;
      if (field.length === 0) continue;
      out[field] = value;
    }
    return out;
  }

  async function handleSubmit(): Promise<void> {
    if (!principalIdentifier.trim()) {
      setError('Principal identifier is required');
      return;
    }
    const corrections = collectCorrections();
    if (Object.keys(corrections).length === 0) {
      setError('At least one field correction is required (field path → new value).');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createRectificationRequest({
        principalIdentifier: principalIdentifier.trim(),
        identifierKind,
        corrections,
        ...(requestReason.trim() ? { requestReason: requestReason.trim() } : {}),
      });
      setPrincipalIdentifier('');
      setRequestReason('');
      setCorrectionRows([newCorrectionRow()]);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'failed to submit rectification request');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleApprove(j: RectificationJob): Promise<void> {
    const reason = window.prompt(
      `Approve rectification for principal ${j.principalIdentifierHash.slice(0, 12)}…?\n` +
      `${Object.keys(j.corrections).length} field(s) to update across ${j.assetsScope.length} asset(s).\n` +
      `Approval reason:`,
    );
    if (!reason) return;
    try {
      await approveRectificationJob(j.id, reason);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'approve failed');
    }
  }

  async function handleExecute(j: RectificationJob): Promise<void> {
    if (!window.confirm(
      `Execute rectification across ${j.assetsScope.length} asset(s)? ` +
      `Each asset will receive an update-intent recorded in the audit chain. ` +
      `Field updates are logged for the regulator audit.`,
    )) return;
    try {
      await executeRectificationJob(j.id);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'execute failed');
    }
  }

  return (
    <PageContainer>
      <PageHeader
        title="Right to rectification (GDPR Art 16 / DPDPA §13)"
        subtitle="Submit, approve, and execute rectification requests. Principal identifier is hashed at submit-time; raw PII never reaches the audit chain. Each correction is logged per-asset for the regulator audit."
        badges={[
          { label: 'DPO-gated', variant: 'glass' },
          { label: 'SHA-256 principal hash', variant: 'glass' },
          { label: 'Per-asset field log', variant: 'glass' },
          ...(loading
            ? [{ label: 'Loading', variant: 'glass' as const }]
            : error
              ? [{ label: 'Degraded', variant: 'warning' as const }]
              : [{ label: 'Live', variant: 'success' as const }]),
        ]}
        actions={
          <Button size="sm" variant="glass" onClick={() => void refresh()} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        }
      />

      {error && (
        <ErrorState
          title="Rectification action failed"
          detail={error}
          onRetry={() => setError(null)}
          retryLabel="Dismiss"
        />
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Pending" value={counts.pending} icon={Clock} glow={counts.pending > 0 ? 'medium' : 'low'} hint="awaiting approval" />
        <KpiCard label="Approved" value={counts.approved} icon={ShieldCheck} glow={counts.approved > 0 ? 'medium' : 'low'} hint="ready to execute" />
        <KpiCard label="Completed" value={counts.completed} icon={CheckCircle2} glow={counts.completed > 0 ? 'high' : 'low'} hint="fields updated" />
        <KpiCard label="Failed" value={counts.failed} icon={XCircle} glow={counts.failed > 0 ? 'critical' : 'low'} hint="see execution log" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Submit request */}
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle className="flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary" />
              Submit rectification request
            </CardTitle>
            <CardDescription>
              The identifier is SHA-256 hashed before persistence. The corrections map is
              persisted verbatim so the auditor can verify exactly what the subject asked us
              to change.
            </CardDescription>
          </CardHeader>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-muted-foreground block mb-0.5">Identifier kind</label>
              <select
                value={identifierKind}
                onChange={(e) => setIdentifierKind(e.target.value as RectificationIdentifierKind)}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              >
                {IDENTIFIER_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Principal identifier</label>
              <input
                type="text"
                value={principalIdentifier}
                onChange={(e) => setPrincipalIdentifier(e.target.value)}
                placeholder={identifierKind === 'aadhaar' ? '12-digit Aadhaar' : identifierKind === 'email' ? 'alice@example.com' : 'value'}
                maxLength={200}
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
              <div className="text-[10px] text-muted-foreground mt-0.5">
                Hashed on submit; never persisted in raw form.
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-muted-foreground">Field corrections</label>
                <Button size="sm" variant="glass" onClick={addCorrectionRow}>
                  <Plus className="h-3 w-3 mr-1" /> Add field
                </Button>
              </div>
              <div className="space-y-2">
                {correctionRows.map((row) => (
                  <div key={row.key} className="flex items-center gap-1">
                    <input
                      type="text"
                      value={row.field}
                      onChange={(e) => updateCorrectionField(row.key, 'field', e.target.value)}
                      placeholder="field path (e.g. address.line1)"
                      className="flex-1 glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40 font-mono"
                    />
                    <input
                      type="text"
                      value={row.value}
                      onChange={(e) => updateCorrectionField(row.key, 'value', e.target.value)}
                      placeholder="new value"
                      className="flex-1 glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
                    />
                    <Button
                      size="sm"
                      variant="glass"
                      disabled={correctionRows.length === 1}
                      onClick={() => removeCorrectionRow(row.key)}
                      title={correctionRows.length === 1 ? 'At least one field required' : 'Remove'}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="text-[10px] text-muted-foreground mt-1">
                Field paths use <code>table.column</code> notation. Validation against your live schema happens at
                execution time via the connected data sources; failed paths are reported per-row in the job log.
              </div>
            </div>
            <div>
              <label className="text-muted-foreground block mb-0.5">Reason (optional)</label>
              <textarea
                value={requestReason}
                onChange={(e) => setRequestReason(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Principal exercised Art 16 right via webform"
                className="w-full glass-lite text-sm rounded px-2 py-1 bg-background/40 border border-border/40"
              />
            </div>
            <div className="flex justify-end pt-1">
              <Button size="sm" onClick={() => void handleSubmit()} disabled={submitting}>
                {submitting
                  ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  : <Edit3 className="h-3.5 w-3.5 mr-1" />}
                Submit request
              </Button>
            </div>
          </div>
        </GlassCard>

        {/* Queue */}
        <GlassCard className="p-5">
          <CardHeader className="p-0 mb-3">
            <CardTitle>Rectification queue</CardTitle>
            <CardDescription>
              Workflow: <strong>pending → approved → running → completed</strong>. Each
              transition is audit-chain logged. Same state machine as erasure.
            </CardDescription>
          </CardHeader>
          {loading && (
            <LoadingState caption="Loading…" />
          )}
          {!loading && jobs.length === 0 && (
            <div className="glass-lite rounded p-6 text-center text-xs text-muted-foreground">
              <Edit3 className="h-6 w-6 mx-auto mb-2 opacity-40" />
              <div>No rectification jobs yet.</div>
            </div>
          )}
          {!loading && jobs.length > 0 && (
            <div className="space-y-2">
              {jobs.map((j, i) => {
                const SIcon = STATUS_ICON[j.status];
                const fieldsCount = Object.keys(j.corrections).length;
                return (
                  <motion.div
                    key={j.id}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: Math.min(i * 0.03, 0.3) }}
                    className="glass-lite rounded p-3 text-xs"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="glass" className="text-[10px] font-mono">{j.identifierKind}</Badge>
                          <Badge variant={STATUS_VARIANT[j.status]} className="text-[10px] inline-flex items-center gap-1">
                            <SIcon className={`h-3 w-3 ${j.status === 'running' ? 'animate-spin' : ''}`} />
                            {j.status}
                          </Badge>
                          <span className="text-[10px] text-muted-foreground">
                            {fieldsCount} field(s) · {j.assetsScope.length} asset(s)
                          </span>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-1 font-mono truncate" title={j.principalIdentifierHash}>
                          {j.principalIdentifierHash.slice(0, 24)}…
                        </div>
                        {j.requestReason && (
                          <div className="text-[10px] mt-1 text-muted-foreground">
                            {j.requestReason}
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <Button size="sm" variant="glass" onClick={() => setDetailJob(j)} title="Details">
                          <Eye className="h-3 w-3" />
                        </Button>
                        {j.status === 'pending' && (
                          <Button size="sm" variant="glass" onClick={() => void handleApprove(j)} title="Approve">
                            <ShieldCheck className="h-3 w-3" />
                          </Button>
                        )}
                        {j.status === 'approved' && (
                          <Button size="sm" variant="glass" onClick={() => void handleExecute(j)} title="Execute">
                            <Play className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </GlassCard>
      </div>

      {/* Detail modal */}
      <Dialog open={detailJob !== null} onOpenChange={(o) => { if (!o) setDetailJob(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Rectification job detail</DialogTitle>
          </DialogHeader>
          {detailJob && (
            <div className="space-y-3 text-xs">
              <div className="grid grid-cols-2 gap-3">
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Status</div>
                  <Badge variant={STATUS_VARIANT[detailJob.status]} className="text-[10px]">{detailJob.status}</Badge>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Identifier kind</div>
                  <code className="font-mono">{detailJob.identifierKind}</code>
                </div>
                <div className="glass-lite rounded p-2 col-span-2">
                  <div className="text-muted-foreground mb-0.5">Principal hash</div>
                  <code className="font-mono text-[10px] break-all">{detailJob.principalIdentifierHash}</code>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Created</div>
                  <code className="font-mono">{new Date(detailJob.createdAt).toLocaleString()}</code>
                </div>
                <div className="glass-lite rounded p-2">
                  <div className="text-muted-foreground mb-0.5">Completed</div>
                  <code className="font-mono">{detailJob.completedAt ? new Date(detailJob.completedAt).toLocaleString() : '-'}</code>
                </div>
              </div>

              <div>
                <div className="font-medium mb-1">Corrections ({Object.keys(detailJob.corrections).length})</div>
                <div className="glass-lite rounded p-2 space-y-1">
                  {Object.entries(detailJob.corrections).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2">
                      <code className="font-mono text-[10px] text-muted-foreground shrink-0">{k}</code>
                      <span className="text-muted-foreground">→</span>
                      <code className="font-mono text-[10px] truncate">{String(v)}</code>
                    </div>
                  ))}
                </div>
              </div>

              {detailJob.approverActorId && (
                <div className="glass-lite rounded p-3">
                  <div className="font-medium mb-1">Approval</div>
                  <div className="text-muted-foreground">
                    By <code className="font-mono">{detailJob.approverActorId.slice(0, 8)}…</code>
                    {detailJob.approverDecidedAt && <> at {new Date(detailJob.approverDecidedAt).toLocaleString()}</>}
                  </div>
                  {detailJob.approverReason && (
                    <div className="mt-1">Reason: <em>{detailJob.approverReason}</em></div>
                  )}
                </div>
              )}

              <div>
                <div className="font-medium mb-1">Assets in scope ({detailJob.assetsScope.length})</div>
                {detailJob.assetsScope.length === 0 ? (
                  <div className="glass-lite rounded p-3 text-muted-foreground italic">
                    The access-graph walk found no assets touching this principal.
                  </div>
                ) : (
                  <div className="glass-lite rounded p-2 max-h-32 overflow-y-auto">
                    {detailJob.assetsScope.map((id) => (
                      <code key={id} className="block font-mono text-[10px]">{id}</code>
                    ))}
                  </div>
                )}
              </div>

              {detailJob.executionLog.length > 0 && (
                <div>
                  <div className="font-medium mb-1">Execution log</div>
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {detailJob.executionLog.map((e, i) => (
                      <div key={i} className="glass-lite rounded p-2 flex items-start gap-2">
                        <Badge
                          variant={e.status === 'ok' ? 'success' : e.status === 'failed' ? 'critical' : 'warning'}
                          className="text-[9px] shrink-0"
                        >
                          {e.status}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-[10px] text-muted-foreground">{e.assetId}</div>
                          <div>{e.detail}</div>
                          {e.fieldsUpdated.length > 0 && (
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              fields: {e.fieldsUpdated.map((f) => <code key={f} className="font-mono mr-1">{f}</code>)}
                            </div>
                          )}
                          <div className="text-[10px] text-muted-foreground">{new Date(e.ts).toLocaleTimeString()} · {e.action}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {detailJob.errorDetail && (
                <div className="glass-lite border border-sev-critical/40 rounded p-2 text-sev-critical">
                  <div className="font-medium">Error</div>
                  <div>{detailJob.errorDetail}</div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
